import '@glideapps/glide-data-grid/dist/index.css'

import { FullScreenContainer } from '@components/Datagrid/components'
import { useCustomCellRenderers } from '@components/Datagrid/customCells'
import {
  type NavigatorOpts,
  useRowAnchorHandler,
  useTooltipContainer,
} from '@components/Datagrid/hooks'
import {
  type CellClickedEventArgs,
  type DataEditorProps,
  type DataEditorRef,
  type DrawHeaderCallback,
  type EditableGridCell,
  type GetRowThemeCallback,
  type GridCell,
  type GridColumn,
  type GridSelection,
  type HeaderClickedEventArgs,
  type Item,
  type Theme,
} from '@glideapps/glide-data-grid'
import CircularProgress from '@mui/material/CircularProgress'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import EmptyState from '@ui/EmptyState'
import range from 'lodash-es/range'
import type { RefObject } from 'react'
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { MobileCardList } from './components/MobileCardList'
import { PreventHistoryBack } from './components/PreventHistoryBack'
import { RowActions, type RowActionsMenuItem } from './components/RowActions'
import { TooltipContainer } from './components/TooltipContainer'
import { headerIcons } from './headerIcons'
import useDatagridChange, {
  type DatagridChange,
  type OnDatagridChange,
} from './hooks/useDatagridChange'
import { useFullScreenMode } from './hooks/useFullScreenMode'
import { useRowAnchor } from './hooks/useRowAnchor'
import { useRowHover } from './hooks/useRowHover'
import { useScrollRight } from './hooks/useScrollRight'
import * as S from './styles'
import { blendOpaque, cellHeight, dataRowHeight, useDatagridTheme } from './styles'
import type { AvailableColumn, RowMarkerOptions } from './types'
import { preventRowClickOnSelectionCheckbox } from './utils'

export interface GetCellContentOpts {
  changes: RefObject<DatagridChange[]>
  added: number[]
  removed: number[]
  getChangeIndex: (column: string, row: number) => number
}

// Matches the shared hover grammar: alpha(primary.main, 0.08)
const rowHoverOpacity = 0.08

interface MenuItemsActions {
  removeRows: (indexes: number[]) => void
}

export interface DatagridRenderHeaderProps {
  isFullscreenOpen: boolean
  toggleFullscreen: () => void
  addRowOnDatagrid: () => void
  isAnimationOpenFinished: boolean
}

interface DatagridProps {
  fillHandle?: boolean
  availableColumns: readonly AvailableColumn[]
  emptyText: string
  /** Suggested next step when there are no rows, e.g. a "Clear filters" button */
  emptyAction?: ReactNode
  getCellError: (item: Item, opts: GetCellContentOpts) => boolean
  getCellContent: (item: Item, opts: GetCellContentOpts) => GridCell
  getColumnTooltipContent?: (colIndex: number) => string
  menuItems: (index: number) => RowActionsMenuItem[]
  rows: number
  loading?: boolean
  selectionActions: (selection: number[], actions: MenuItemsActions) => ReactNode
  onChange?: OnDatagridChange
  onHeaderClicked?: (colIndex: number, event: HeaderClickedEventArgs) => void
  renderColumnPicker?: () => ReactElement
  renderRowActions?: (index: number) => ReactElement
  rowActionBarWidth?: number
  onRowClick?: (item: Item) => void
  onColumnMoved?: (startIndex: number, endIndex: number) => void
  onColumnResize?: (column: GridColumn, newSize: number) => void
  onRowSelectionChange?: (rowsId: number[], clearSelection: () => void) => void
  readonly?: boolean
  hasRowHover?: boolean
  rowMarkers?: RowMarkerOptions['kind']
  freezeColumns?: DataEditorProps['freezeColumns']
  verticalBorder?: DataEditorProps['verticalBorder']
  columnSelect?: DataEditorProps['columnSelect']
  showEmptyDatagrid?: boolean
  rowAnchor?: (item: Item) => string
  rowHeight?: number | ((index: number) => number)
  actionButtonPosition?: 'left' | 'right'
  recentlyAddedColumn?: string | null
  onClearRecentlyAddedColumn?: () => void
  renderHeader?: (props: DatagridRenderHeaderProps) => ReactNode
  navigatorOpts?: NavigatorOpts
  showTopBorder?: boolean
  themeOverride?: Partial<Theme>
}

// Helper to get cell action
const getCellAction = (columns: readonly AvailableColumn[] | undefined, columnIndex: number) => {
  if (!columns || columnIndex < 0 || columnIndex >= columns.length) {
    return false
  }

  const column = columns[columnIndex]
  return column?.action ? column.action(column.id) : false
}

const Datagrid = ({
  availableColumns,
  emptyText,
  emptyAction,
  getCellContent,
  getCellError,
  menuItems,
  rows,
  selectionActions,
  onHeaderClicked,
  onChange,
  renderColumnPicker,
  renderRowActions,
  rowActionBarWidth = 36,
  onRowClick,
  getColumnTooltipContent,
  readonly = false,
  rowMarkers: rowMarkersKind = 'checkbox',
  freezeColumns = 1,
  verticalBorder,
  columnSelect = 'none',
  onColumnMoved,
  onColumnResize,
  showEmptyDatagrid = false,
  loading,
  rowAnchor,
  hasRowHover = false,
  onRowSelectionChange,
  actionButtonPosition = 'left',
  recentlyAddedColumn,
  onClearRecentlyAddedColumn,
  rowHeight = dataRowHeight,
  renderHeader,
  navigatorOpts,
  showTopBorder = true,
  themeOverride,
  ...datagridProps
}: DatagridProps): ReactElement => {
  const muiTheme = useTheme()
  // Phone widths swap the canvas grid for a card list (shared across every
  // list page) — a 6-column canvas at 390px is unreadable and barely tappable.
  const isMobileCardLayout = useMediaQuery(muiTheme.breakpoints.down('sm'))
  const datagridTheme = useDatagridTheme()
  const finalTheme = useMemo(
    () => ({ ...datagridTheme, ...themeOverride }),
    [datagridTheme, themeOverride],
  )

  const editor = useRef<DataEditorRef | null>(null)
  const customRenderers = useCustomCellRenderers()
  const { isScrolledToRight } = useScrollRight()
  const { isOpen, isAnimationOpenFinished, toggle } = useFullScreenMode()
  const { clearTooltip, tooltip, setTooltip } = useTooltipContainer()
  const [selection, setSelection] = useState<GridSelection>()
  const [areCellsDirty, setCellsDirty] = useState(true)

  const { rowAnchorRef, setRowAnchorRef, setAnchorPosition } = useRowAnchor({
    availableColumns,
    getRowAnchorUrl: rowAnchor,
    rowMarkers: rowMarkersKind,
  })
  const rowAnchorHandler = useRowAnchorHandler(navigatorOpts)

  const rowMarkers = useMemo(
    (): RowMarkerOptions => ({
      kind: rowMarkersKind,
      theme: {
        accentColor: muiTheme.palette.primary.main,
      },
      width: 48,
    }),
    [rowMarkersKind, muiTheme.palette.primary.main],
  )

  const { handleRowHover, hoverRow } = useRowHover({
    hasRowHover,
    onRowHover: setAnchorPosition,
  })

  // Allow to listen to which row is selected and notify parent component
  useEffect(() => {
    if (onRowSelectionChange && selection) {
      onRowSelectionChange(Array.from(selection.rows), () => {
        setSelection(undefined)
      })
    }
  }, [onRowSelectionChange, selection])

  useEffect(() => {
    if (recentlyAddedColumn && editor.current) {
      const columnIndex = availableColumns.findIndex((column) => column.id === recentlyAddedColumn)

      if (columnIndex === -1) {
        return
      }

      const datagridScroll = editor.current.scrollTo

      datagridScroll(columnIndex, 0, 'horizontal', 0, 0, { hAlign: 'start' })

      if (typeof onClearRecentlyAddedColumn === 'function') {
        onClearRecentlyAddedColumn()
      }
    }
  }, [recentlyAddedColumn, availableColumns, onClearRecentlyAddedColumn])

  const { added, onCellEdited, onRowsRemoved, changes, removed, getChangeIndex, onRowAdded } =
    useDatagridChange(availableColumns, rows, onChange, (areCellsDirty: boolean) =>
      setCellsDirty(areCellsDirty),
    )
  const rowsTotal = rows - removed.length + added.length
  const hasMenuItem = !!menuItems(0).length
  const hasColumnGroups = availableColumns.some((col) => col.group)

  const handleGetCellContent = useCallback(
    ([column, row]: Item): GridCell => {
      const item = [column, row] as const
      const opts = {
        added,
        changes,
        getChangeIndex,
        removed,
      }
      const columnId = availableColumns[column]?.id
      const hasChanged = !!changes.current[getChangeIndex(columnId, row)]?.data

      return {
        ...getCellContent(item, opts),
        ...(hasChanged && areCellsDirty
          ? {
              themeOverride: {
                bgCell:
                  muiTheme.palette.mode === 'light'
                    ? 'hsla(215, 100%, 96%, 1)'
                    : 'hsla(215, 100%, 21%, 1)',
              },
            }
          : {}),
        ...(getCellError(item, opts)
          ? {
              themeOverride: {
                bgCell: muiTheme.palette.error.light,
              },
            }
          : {}),
      }
    },
    [
      muiTheme.palette.mode,
      muiTheme.palette.error.light,
      changes,
      added,
      removed,
      getChangeIndex,
      availableColumns,
      getCellContent,
      areCellsDirty,
      getCellError,
    ],
  )

  const handleOnCellEdited = useCallback(
    ([column, row]: Item, newValue: EditableGridCell): void => {
      onCellEdited([column, row], newValue)

      if (!editor.current) {
        return
      }

      editor.current.updateCells(
        range(availableColumns.length).map((offset) => ({
          cell: [column + offset, row],
        })),
      )
    },
    [onCellEdited, availableColumns],
  )

  const handleCellClick = useCallback(
    (item: Item, args: CellClickedEventArgs) => {
      if (preventRowClickOnSelectionCheckbox(rowMarkersKind, item[0])) {
        return
      }

      const shouldIntentToOpenInNewTab = args.metaKey || args.ctrlKey

      if (onRowClick && !shouldIntentToOpenInNewTab) {
        onRowClick(item)
        return
      }

      if (getCellAction(availableColumns, item[0])) {
        return
      }

      handleRowHover(args)

      if (rowAnchorRef.current) {
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          ctrlKey: args.ctrlKey,
          metaKey: args.metaKey,
          shiftKey: args.shiftKey,
        })

        rowAnchorRef.current.dispatchEvent(clickEvent)
      }
    },
    [rowMarkersKind, onRowClick, availableColumns, handleRowHover, rowAnchorRef],
  )

  const handleGridSelectionChange = (gridSelection: GridSelection) => {
    if (readonly && !gridSelection.current) {
      setSelection(gridSelection)
    }

    if (!readonly) {
      setSelection(gridSelection)
    }
  }

  const rowOverrideTheme = useMemo((): Partial<Theme> => {
    const overrideTheme: Partial<Theme> = {
      // Same faint teal wash the rest of the app hovers with, pre-blended over
      // the canvas because glide paints bgCell opaquely.
      bgCell: blendOpaque(
        muiTheme.palette.primary.main,
        muiTheme.palette.background.default,
        rowHoverOpacity,
      ),
      bgCellMedium: muiTheme.palette.action.hover,
    }

    if (readonly) {
      overrideTheme.accentLight = muiTheme.palette.background.default
    }

    return overrideTheme
  }, [muiTheme, readonly])

  const handleGetThemeOverride = useCallback<GetRowThemeCallback>(
    (row: number) => {
      if (row !== hoverRow) {
        return undefined
      }

      return rowOverrideTheme
    },
    [hoverRow, rowOverrideTheme],
  )

  const handleHeaderClicked = useCallback(
    (colIndex: number, event: HeaderClickedEventArgs) => {
      if (getColumnTooltipContent) {
        const content = getColumnTooltipContent(colIndex)

        if (content) {
          setTooltip(content, event.bounds)
        }
      }

      if (onHeaderClicked) {
        onHeaderClicked(colIndex, event)
      }
    },
    [getColumnTooltipContent, onHeaderClicked, setTooltip],
  )

  const drawHeader: DrawHeaderCallback = useCallback((args, next) => {
    const { ctx, rect, isSelected, spriteManager, theme, column } = args

    if (isSelected && column.id !== 'empty') {
      const iconSize = 16
      const padding = 8
      const x = rect.x + rect.width - iconSize - padding
      const y = rect.y + (rect.height - iconSize) / 2

      spriteManager.drawSprite('gripVertical', 'normal', ctx, x, y, iconSize, theme)
    }

    next()
  }, [])

  const handleRemoveRows = useCallback(
    (rows: number[]) => {
      if (selection?.rows) {
        onRowsRemoved(rows)
        setSelection(undefined)
      }
    },
    [selection, onRowsRemoved],
  )

  const handleColumnResize = useCallback(
    (column: GridColumn, newSize: number) => {
      if (tooltip) {
        clearTooltip()
      }

      if (!onColumnResize) {
        return
      }

      onColumnResize(column, newSize)
    },
    [clearTooltip, onColumnResize, tooltip],
  )

  const handleColumnMoved = useCallback(
    (startIndex: number, endIndex: number) => {
      if (tooltip) {
        clearTooltip()
      }

      if (!onColumnMoved) {
        return
      }

      onColumnMoved(startIndex, endIndex)
    },
    [clearTooltip, onColumnMoved, tooltip],
  )

  const selectionActionsComponent = useMemo(
    () =>
      selection?.rows && selection?.rows.length > 0
        ? selectionActions(Array.from(selection.rows), {
            removeRows: handleRemoveRows,
          })
        : null,
    [selection, selectionActions, handleRemoveRows],
  )

  const showLinkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const hideLinkAndShowAfterDelay = useCallback(() => {
    if (showLinkTimerRef.current) {
      clearTimeout(showLinkTimerRef.current)
    }

    if (rowAnchorRef.current) {
      rowAnchorRef.current.style.display = 'none'
    }

    showLinkTimerRef.current = setTimeout(() => {
      if (rowAnchorRef.current) {
        rowAnchorRef.current.style.display = 'block'
      }
    }, 100)
  }, [rowAnchorRef])

  if (loading) {
    return (
      <S.LoaderContainer data-test-id="datagrid-loader">
        <CircularProgress />
      </S.LoaderContainer>
    )
  }

  const emptyStateComponent = (
    <S.EmptyContainer>
      <EmptyState
        title={<span data-test-id="empty-data-grid-text">{emptyText}</span>}
        action={emptyAction}
      />
    </S.EmptyContainer>
  )

  return (
    <>
      <FullScreenContainer isOpen={isOpen}>
        <PreventHistoryBack isFullHeight={isOpen}>
          <S.DatagridCard isOpen={isOpen}>
            {renderHeader?.({
              addRowOnDatagrid: onRowAdded,
              isAnimationOpenFinished,
              isFullscreenOpen: isOpen,
              toggleFullscreen: toggle,
            })}
            <S.DatagridCardContent data-test-id="list">
              {isMobileCardLayout ? (
                rowsTotal > 0 ? (
                  <MobileCardList
                    availableColumns={availableColumns}
                    getCellContent={handleGetCellContent}
                    menuItems={menuItems}
                    onAnchorClick={rowAnchorHandler}
                    onRowClick={onRowClick}
                    rowAnchor={rowAnchor}
                    rows={rowsTotal}
                  />
                ) : (
                  emptyStateComponent
                )
              ) : (
                <>
                  {selection?.rows && selection?.rows.length > 0 && selectionActionsComponent && (
                    <S.ActionBtnBar actionButtonPosition={actionButtonPosition}>
                      {selectionActionsComponent}
                    </S.ActionBtnBar>
                  )}
                  <S.EditorContainer>
                    <S.DatagridTopBorder showTopBorder={showTopBorder} />
                    <S.DatagridEditor
                      {...datagridProps}
                      customRenderers={customRenderers}
                      verticalBorder={verticalBorder}
                      headerIcons={headerIcons}
                      drawHeader={drawHeader}
                      datagridTheme={finalTheme}
                      getCellContent={handleGetCellContent}
                      onCellEdited={handleOnCellEdited}
                      columns={availableColumns}
                      rows={rowsTotal}
                      freezeColumns={freezeColumns}
                      smoothScrollX
                      rowMarkers={rowMarkers}
                      rowSelect="multi"
                      rowSelectionMode="multi"
                      rangeSelect="multi-rect"
                      columnSelect={columnSelect}
                      getCellsForSelection
                      onColumnMoved={handleColumnMoved}
                      onColumnResize={handleColumnResize}
                      onHeaderClicked={handleHeaderClicked}
                      onCellClicked={handleCellClick}
                      onGridSelectionChange={handleGridSelectionChange}
                      onItemHovered={handleRowHover}
                      getRowThemeOverride={handleGetThemeOverride}
                      gridSelection={selection}
                      rowHeight={rowHeight}
                      headerHeight={cellHeight}
                      ref={editor}
                      onPaste
                      rightElementProps={{
                        sticky: true,
                      }}
                      rightElement={
                        <S.RowActionBar
                          width={rowActionBarWidth}
                          isScrolledToRight={isScrolledToRight}
                        >
                          <S.RowActionBarShadow isActive={!isScrolledToRight} />
                          {hasMenuItem ? (
                            <S.ColumnPicker>
                              {renderColumnPicker ? renderColumnPicker() : null}
                            </S.ColumnPicker>
                          ) : (
                            <S.ColumnPickerBackground>
                              {renderColumnPicker ? renderColumnPicker() : null}
                            </S.ColumnPickerBackground>
                          )}
                          {hasColumnGroups && (
                            <S.RowColumnGroup isScrolledToRight={isScrolledToRight} />
                          )}
                          {hasMenuItem &&
                            Array(rowsTotal)
                              .fill(0)
                              .map((_, index) =>
                                renderRowActions ? (
                                  renderRowActions(index)
                                ) : (
                                  <RowActions
                                    key={`row-actions-${index}`}
                                    menuItems={menuItems(index)}
                                    disabled={index >= rowsTotal - added.length}
                                  />
                                ),
                              )}
                        </S.RowActionBar>
                      }
                    />
                    {hasColumnGroups && <S.ColumnGroupFixer />}
                  </S.EditorContainer>
                  {/* The grid above still renders its header row, so the table
                      structure survives an empty result set. */}
                  {rowsTotal === 0 && !showEmptyDatagrid ? emptyStateComponent : null}
                </>
              )}
            </S.DatagridCardContent>
          </S.DatagridCard>
          <TooltipContainer
            clearTooltip={clearTooltip}
            bounds={tooltip?.bounds}
            title={tooltip?.title}
          />
          {rowAnchor ? (
            <S.DatagridRowAnchor
              ref={setRowAnchorRef}
              tabIndex={-1}
              aria-hidden={true}
              onWheelCapture={hideLinkAndShowAfterDelay}
              onClick={rowAnchorHandler}
            />
          ) : null}
        </PreventHistoryBack>
      </FullScreenContainer>
      <S.DatagridPortal />
    </>
  )
}

export default Datagrid
